import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import useForm from '../hooks/useForm';
import getFormattedNumber from '../functions/getFormattedNumber';
import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import Typography from '../definitions/Typography';
import MessagePropType from '../definitions/MessagePropType';
import Paragraph from './Paragraph';
import CalculationPanel from './CalculationPanel';
import TooltipAnchor from './TooltipAnchor';
import { BOTTOM, MIDDLE, LOCATION_OPTIONS, ALIGNMENT_OPTIONS } from './Tooltip';


const OPERATOR_TYPES = {
    NONE: 'none',
    PLUS: 'plus',
    MINUS: 'minus',
    TIMES: 'times',
    EQUALS: 'equals',
};

const OPERATORS = {
    [OPERATOR_TYPES.NONE]: '',
    [OPERATOR_TYPES.PLUS]: '+',
    [OPERATOR_TYPES.MINUS]: '−',
    [OPERATOR_TYPES.TIMES]: '×',
    [OPERATOR_TYPES.EQUALS]: '=',
};

function CalculationLine({
    id,
    name,

    isVisible,

    label,

    operandValue,
    operator,
    operandFormat,
    result,

    tooltipMessage,
    tooltipWidth,
    tooltipLocation,
    nubbinAlignment,
}) {
    const intl = useIntl();
    const form = useForm();
    const isInput = typeof operandValue === 'string';
    const isFunction = typeof operandValue === 'function';

    // Register the function to the Form.
    useEffect(() => {
        if (name && form && isFunction) {
            form.addComputed(name, operandValue);

            return () => form.removeComputed(name);
        }

    }, [ name, form, operandValue ]);

    const operand = isInput
        ? form.getValue(operandValue)
        : isFunction
            ? operandValue(form.values)
            : operandValue;

    // ASSUMPTION: these values are always
    // formatted as numbers, be they decimal, percentage or dollars.
    const formattedOperand = getFormattedNumber(operand, operandFormat, intl);

    return (
        <If condition={isVisible}>
            <CalculationLine.Container
                $result={result}
                isInput={isInput}
                data-test-name={`calculation-line-${name}`}
            >
                <div className="inner">
                    <Paragraph
                        className="operator"
                        message={OPERATORS[operator]}
                        size="medium"

                    />

                    <Paragraph
                        className="label"
                        message={label}
                        size="medium"
                        id={id}
                    />

                    <If condition={tooltipMessage}>
                        <TooltipAnchor
                            className="tooltip"
                            message={tooltipMessage}
                            tooltipWidth={tooltipWidth}
                            tooltipLocation={tooltipLocation}
                            nubbinAlignment={nubbinAlignment}
                        />
                    </If>

                    <Paragraph
                        className="operand"
                        aria-labelledby={id}
                        size="medium"
                        message={formattedOperand}
                    />
                </div>
            </CalculationLine.Container>
        </If>
    );
}

CalculationLine.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string,

    isVisible: PropTypes.bool,

    label: MessagePropType.isRequired,

    operandValue: PropTypes.oneOfType([
        PropTypes.string, // Name of the registered form value
        PropTypes.number, // If the value isn't derived from anything
        PropTypes.func, // A possible equation to be added as a computed within the form
    ]).isRequired,
    operator: PropTypes.oneOf(Object.values(OPERATOR_TYPES)),
    operandFormat: PropTypes.string, // Any intl formatter
    result: PropTypes.bool,

    tooltipMessage: MessagePropType,
    tooltipWidth: PropTypes.string, // A fixed length unit like em or rem
    tooltipLocation: PropTypes.oneOf(LOCATION_OPTIONS),
    nubbinAlignment: PropTypes.oneOf(ALIGNMENT_OPTIONS),
};

CalculationLine.defaultProps = {
    name: undefined,

    isVisible: true,

    operator: OPERATOR_TYPES.NONE,
    operandFormat: 'CAD0',
    result: false,

    tooltipMessage: undefined,
    tooltipWidth: '30rem',
    tooltipLocation: BOTTOM,
    nubbinAlignment: MIDDLE,
};

CalculationLine.Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-around;

    // Round corners of last row otherwise they'll peek out though the
    // rounded corners of the parent CalculationPanel. Particularily noticeable
    // when last row has a background colour.
    &:last-child {
        border-bottom-left-radius: ${CalculationPanel.PANEL_BORDER_RADIUS};
        border-bottom-right-radius: ${CalculationPanel.PANEL_BORDER_RADIUS};
    }

    .inner {
        display: flex;
        align-items: center;

        flex: 1;

        max-width: 31.25rem; //500px

        margin-left: 5%;
        margin-right: 5%;

        > .operator,
        > .label,
        > .operand {
            // Linearly scale font size based on screen width.
            // Resource: https://css-tricks.com/linearly-scale-font-size-with-css-clamp-based-on-the-viewport/
            // Values subbed with known max/min widths and fonts
            //
            // Need to figure out best approach for rem/em based units. Makes it complicated for the reader to understand
            // right now as min would be based off 14px, and max would be based off 16px as we are already changing font-size
            // via media queries globally
            font-size: clamp(12px, -0.875rem + 2.666vw, 16px);
        }

        > .operator {
            min-width: 8%;
            font-weight: ${Typography.WEIGHTS.MEDIUM};
            color: ${Colours.BLUEBERRY_DARK};

            margin: ${Sizes.SPACING.HALF} 0;
        }

        > .label {
            flex: 1 1 10rem;

            margin: ${Sizes.SPACING.HALF} 0;
        }

        > .tooltip {
            display: flex;
            align-items: center;
            margin-left: 5%;
        }

        > .operand {
            flex: 1.5;
            min-width: ${Sizes.SPACING.FIVE};
            text-align: right;
            color: ${Colours.BLACKBERRY};
            margin: ${Sizes.SPACING.HALF} 0 ${Sizes.SPACING.HALF} 5%;
        }
    }

    ${props => props.$result && `
        background-color: ${Colours.BLUEBERRY_LIGHTEST};
        border-top: 1px solid ${Colours.BLUEBERRY_LIGHTEST};

        margin: ${Sizes.SPACING.ONE} 0 ${Sizes.SPACING.ONE};
        padding: ${Sizes.SPACING.ONE} 0;

        &:last-child {
            margin-bottom: 0;
        }

        .inner > .label {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
            color: ${Colours.BLUEBERRY_DARK};

            flex: 1 1 10rem;
        }

        .inner > .operand {
            flex: 2;

            font-weight: ${Typography.WEIGHTS.MEDIUM};
            color: ${Colours.BLUEBERRY_DARK};
        }
    `}

    ${props => props.isInput && `
        > .operand {
            color: ${Colours.BLUEBERRY_DARK};
        }
    `}
`;

export default observer(CalculationLine);
